import { Flame } from 'lucide-react'
import { stripLabel } from '../habits/HabitKeepingUp'
import type { Skill, SkillBoardCard } from '../../services/skillService'
import { Timestamp } from '../ui/Timestamp'
import { SkillLadder } from './SkillLadder'
import { capabilityOf } from '../../utils/skillWords'
import { keepingUpWords } from '../../utils/keepingUpWords'

/**
 * How a skill has been going, in one glance (Skills UX pass 2026-09-06): the
 * ladder with what the current rung means, the last fourteen days as dots,
 * sessions in the last thirty, the last time, how it felt, and the path's
 * progress. Tone: what happened, never a scolding.
 */
export function Last14Practice({ days, size = 'md' }: { days: Array<{ date: string; outcome: 'done' | null }>; size?: 'sm' | 'md' }) {
  const dim = size === 'sm' ? 'h-2 w-2' : 'h-2.5 w-2.5'
  return (
    <span className="inline-flex items-center gap-1" aria-label={stripLabel(days.length)} title={stripLabel(days.length)} data-testid="skill-last-14">
      {days.map((d) => (
        <span key={d.date} title={`${d.date} · ${d.outcome ? 'practised' : 'no practice'}`} className={`${dim} rounded-full ${d.outcome ? 'bg-ok' : 'border border-soft bg-transparent'}`} />
      ))}
    </span>
  )
}

export function SkillKeepingUp({ skill, card, headingClass }: { skill: Skill; card: SkillBoardCard | null; headingClass: string }) {
  const capability = capabilityOf(skill.current_level)
  // The ladder draws only once a level is asserted (2026-09-12): with nothing
  // set it read "Where you are / Where you want to get" — a fourth prompt for
  // a gap the summary line, the Add row and the gaps card already carry.
  const hasLevel = Boolean(skill.current_level || skill.target_level)
  return (
    <section data-testid="skill-keeping-up">
      <h3 className={headingClass}><Flame className="h-3.5 w-3.5 text-brand" /> Keeping up</h3>
      {hasLevel && <div className="mt-2"><SkillLadder current={skill.current_level} target={skill.target_level} words /></div>}
      {hasLevel && capability && <p className="mt-1 text-app-meta text-secondary" data-testid="skill-capability">{capability}</p>}
      {card && (
        <>
          <div className="mt-3"><Last14Practice days={card.last_14} /></div>
          <p className="mt-2 text-sm text-primary" data-testid="skill-keeping-up-words">
            {keepingUpWords(card, { noun: 'practice day', done: '', quiet: 'A while since the last one', none: 'The first practice is still ahead' })}
          </p>
          <dl className="mt-1 space-y-0.5 text-app-meta text-secondary">
            {card.last_at && <div>Last <Timestamp value={card.last_at} mode="datetime" /></div>}
            {!card.last_at && card.last_date && <div>Last <Timestamp value={`${card.last_date}T12:00:00`} mode="date" /></div>}
            {card.items.habits.some((h) => h.live) && <div>{card.items.habits.filter((h) => h.live).length === 1 ? 'One habit keeps' : `${card.items.habits.filter((h) => h.live).length} habits keep`} it going</div>}
            {/* The path's count lives on the Journey heading (one fact, one place). */}
          </dl>
        </>
      )}
    </section>
  )
}

export default SkillKeepingUp
